import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

/** A panel that drops down from a button, like a menu but with any content. Closes on Escape or a click elsewhere. */
export function Popover({
  anchor,
  onClose,
  children,
  className = "",
  align = "start",
  label,
}: {
  /** The button it hangs from. */
  anchor: HTMLElement;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  /** Line up with the anchor's left edge (start) or right edge (end). */
  align?: "start" | "end";
  label?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useLayoutEffect(() => {
    const a = anchor.getBoundingClientRect();
    const el = ref.current;
    const w = el?.offsetWidth ?? 0;
    const h = el?.offsetHeight ?? 0;
    let x = align === "end" ? a.right - w : a.left;
    let y = a.bottom + 4;
    x = Math.max(8, Math.min(x, innerWidth - w - 8));
    if (y + h > innerHeight - 8) y = Math.max(8, a.top - h - 4);
    setPos({ x, y });
  }, [anchor, align]);

  useEffect(() => {
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent) {
        if (e.key !== "Escape") return;
        e.stopPropagation();
      } else if (e instanceof MouseEvent && (ref.current?.contains(e.target as Node) || anchor.contains(e.target as Node))) return;
      closeRef.current();
    };
    window.addEventListener("mousedown", close, true);
    window.addEventListener("keydown", close, true);
    return () => {
      window.removeEventListener("mousedown", close, true);
      window.removeEventListener("keydown", close, true);
    };
  }, [anchor]);

  return createPortal(
    <div
      ref={ref}
      className={`popover ${className}`}
      role="dialog"
      aria-label={label}
      style={pos ? { left: pos.x, top: pos.y } : { left: -9999, top: -9999 }}
    >
      {children}
    </div>,
    document.body,
  );
}
